/**
 * The sampler keeps its take as raw mono floats, which nothing outside the
 * browser can open. WAV is the plainest thing that every editor will read, and
 * 16-bit PCM is what the VSS-30's own samples would have been anyway.
 */

const HEADER_BYTES = 44;
const BITS = 16;

function writeText(view: DataView, offset: number, text: string): void {
  for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
}

export function encodeWav(samples: Float32Array, sampleRate: number, channels = 1): Blob {
  const bytes = samples.length * 2;
  const view = new DataView(new ArrayBuffer(HEADER_BYTES + bytes));

  writeText(view, 0, 'RIFF');
  view.setUint32(4, HEADER_BYTES - 8 + bytes, true);
  writeText(view, 8, 'WAVE');

  writeText(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // PCM header length
  view.setUint16(20, 1, true); // uncompressed
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, (sampleRate * channels * BITS) / 8, true); // bytes per second
  view.setUint16(32, (channels * BITS) / 8, true); // bytes per frame
  view.setUint16(34, BITS, true);

  writeText(view, 36, 'data');
  view.setUint32(40, bytes, true);

  for (let i = 0; i < samples.length; i += 1) {
    // Clamped before scaling: a normalised take can sit a hair over 1, and
    // letting that wrap would put a click in the loudest part of the sample.
    const sample = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(HEADER_BYTES + i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }

  return new Blob([view], { type: 'audio/wav' });
}

/** Hands a blob to the browser as a download. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.style.display = 'none';
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    link.remove();
    URL.revokeObjectURL(url);
  }, 1000);
}
